import { useEffect, useState } from 'react';
import type { CombatState } from '../types/interfaces';

const TURN_TIMEOUT = 30; // seconds — must match CombatWebSocketServer::TURN_TIMEOUT

/**
 * Returns the seconds remaining in the current player's turn, or null when
 * it is an AI turn, the combat is finished, or no timestamp is available.
 * The value updates every second via setInterval.
 *
 * @param combat   - Current combat state from the WS store (null while loading).
 * @param isMyTurn - True when this client's entity is the active one.
 *                   When false in a co-op game the timer still shows so the
 *                   player can see how long the teammate has left.
 */
export function useTurnTimer(combat: CombatState | null, _isMyTurn: boolean): number | null {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!combat || combat.phase === 'FINISHED' || !combat.turn_started_at) {
      setSecondsLeft(null);
      return;
    }

    // Verify the active entity is a player (not AI)
    const activeEntity = combat.active_entity_id
      ? combat.entities[combat.active_entity_id]
      : null;
    if (!activeEntity || activeEntity.team !== 'player') {
      setSecondsLeft(null);
      return;
    }

    const compute = () => {
      const elapsed = Math.floor(Date.now() / 1000) - combat.turn_started_at!;
      setSecondsLeft(Math.max(0, TURN_TIMEOUT - elapsed));
    };

    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [combat?.turn_started_at, combat?.active_entity_id, combat?.phase]);

  return secondsLeft;
}
